import {
  Route,
  Routes,
  useLocation,
  Navigate,
  matchPath,
  Outlet,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

import "./App.css";
import "./styles/inventory/inventory-globals.css";
import "./utils/dayjs-setup";

// PORTAL
import ScrollToTop from "./components/ScrollToTop";
import LoginPage from "./pages/login/LoginPage";
import RecoverPassword from "./pages/login/RecoverPassword";
import VerifyOtp from "./pages/login/VerifyOtp";
import ResetPassword from "./pages/login/resetPassword";
import ResetCodeError from "./pages/login/ResetCodeError";
import PasswordUpdated from "./pages/login/PasswordUpdated";
import AuthLayoutRoute from "./components/auth/AuthLayoutRoute";
import ProtectedRoute from "./utils/protectedRoutes";
import { Navbar, NavbarLinks } from "./components/Navbar";
import Admins from "./pages/admin/admins";
import CreatePatient from "./pages/patient-page/createpatient";
import RingCentralWidget from "./components/ringCentral";
import BillingPage from "./pages/billing/BillingPage";
import PatientDetail from "./pages/patient-page/patientDetail";
import Dashboards from "./pages/dashboards/dashboards";
import SatffPage from "./pages/staff/staffPage";
import LocationDetail from "./pages/admin/locationDetail";
import ComingSoon from "./components/not-found/comingSoon";
import NotFound from "./components/not-found/notFound";
import SessionExpirationHandler from "./components/sessionExpirationHandler";
import CaseManagerDashboard from "./pages/dashboards/case-manager/caseManagerDashboard";
import ReadingReview from "./pages/dashboards/case-manager/ReadingReview";
import EsclatedReview from "./pages/dashboards/case-manager/EsclatedReview";
import CareplanViewPdf from "./components/patient-detail/careplan-view-pdf";
import ReadingViewPdf from "./components/patient-detail/reading-pdf-view";
import PatientDashboard from "./pages/dashboards/patient/Dashboard";
import CaseManagerCreatePatient from "./pages/dashboards/case-manager/CaseManagerCreatePatient";
import BillingSettingPage from "./pages/billing/setting";
import CaseManagerAuditReport from "./components/pdf/case-manager-audit-report";
import AddOrganization from "./pages/admin/add-organization/";
import InstructionalVideo from "./pages/admin/instructional-video-dashboard/instructional-video";
import VideoWatchPage from "./pages/admin/instructional-video-dashboard/video-watch/VideoWatchPage";
import DeviceOrderedPage from "./pages/devices";
import UserPage from "./pages/user";
import MyTasks from "./pages/patient-page/dailies";
import SuperAdminDashboardPage from "./pages/super-admin-dashboard/SuperAdminDashboardPage";
import InventoryLayout from "./components/inventory/modules/Layout/InventoryLayout";
import TenantDeviceDistributionPage from "./pages/inventory/TenantDeviceDistributionPage";
import OORSummaryViewPdf from "./components/case-manager/reading-reviews/OORSummaryViewPdf";
import OutOfRangePDFView from "./components/case-manager/out-of-range-pdf-view";
import HelpDocumentationPage from "./components/help-documentation/HelpDocumentationPage";
import PrivacyPage from "./pages/privacy";
import TermsPage from "./pages/terms";
import { getUserIdentity } from "./utils/authorization";
// Authorization
import RouteAutoGuard, { MyTasksGuard } from "./utils/RouteAutoGuard";
import { Navigate as Nav } from "react-router-dom";

// PDF Views
import BillingReportPDF from "./components/pdf/billing-pdf";
import DailiesPatientDetails from "./pages/patient-page/dailies/dailiesPatientDetails";
import ShareAuditReportPage from "./components/pdf/audit-report-share";
import useRoles from "./hooks/useRoles";

// Office Inventory
import OfficeInventory from "./pages/officeInventory";

// Inventory Pages
import InventoryDashboardPage from "./pages/inventory/InventoryDashboardPage";
import DeviceManagementPage from "./pages/inventory/DeviceManagementPage";
import SupplyManagementPage from "./pages/inventory/SupplyManagementPage";
import ReportsPage from "./pages/inventory/ReportsPage";
import WarrantyPage from "./pages/inventory/WarantyPage";
import OrdersPage from "./pages/inventory/OrdersPage";
import ComponentUsageGuide from "./components/inventory/modules/components-usage-guide";
import { initializeSessionValues } from "./utils/initSession";
import DeviceSearchResultsPage from "./pages/inventory/DeviceSearchResultsPage";
import FinalizeCareplanPDFView from "./components/patient-detail/finalizecareplan-pdf/FinalizeCareplanPDFView";

// ============================================================
// Route configuration helper
// ============================================================
const routeConfigurations = (roles) => ({
  declaredRoutes: [
    "/",
    "/login",
    "/dashboard",
    "/staff",
    "/patient/create-patient",
    "/patient/details",
    "/admin",
    "/admin/locations/:id",
    "/billings",
    "/device-orders",
    "/billing-settings",
    "/settings",
    "/devices",
    "/reports",
    "/admin/dashboard",
    "/admin/add-organization",
    "/user-settings",
    "/my-tasks/*",
    "/office-inventory",
    "/inventory",
    "/inventory/detail",
    "/inventory/dashboard",
    "/inventory/devices",
    "/inventory/supplies",
    "/inventory/reports",
    "/inventory/status",
  ],
  hideNavbar: [
    "/",
    "/login/*",
    "/login",
    "/recover-password",
    "/verify-otp",
    "/reset-password",
    "/reset-code-error",
    "/password-updated",
    "/privacy",
    "/terms",
    "/inventory/dashboard",
    "/inventory/devices",
    "/inventory/supplies",
    "/inventory/reports",
    "/inventory/status",
    "/inventory/warranty",
    "/inventory/support",
    "/inventory/orders",
    "inventory/device-distribution",
    "inventory/device-search",
  ],
  hideNavbarLinks: [
    "/patient/details/*",
    "/case-manager",
    "/patient-dashboard",
    "/inventory/dashboard",
    "/inventory/devices",
    "/inventory/supplies",
    "/inventory/reports",
    "/inventory/status",
    "/inventory/warrenty",
    "/inventory/support",
    roles.includes("Patient") ? "/dashboard" : "",
    getUserIdentity.isPatient ? "/dashboard" : "",
  ],
});

// ============================================================
// MedicalBillerGuard — handles special business rule
// (keeps Medical Billers out of case-manager / billing-settings)
// ============================================================
function MedicalBillerGuard({ children }) {
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch (e) {
      return {};
    }
  })();

  // const position = user?.account_position?.name;
  // const roles = user?.account_roles?.map((r) => r.name) || [];
  // const isBiller = position === "Medical Biller";
  const isSuperUser = getUserIdentity().isSuperUser || false;
  const isAccountAdmin = getUserIdentity().isAccountAdmin || false;
  const isBiller = getUserIdentity().isMedicalBiller || false;
  if (isBiller && (isSuperUser || isAccountAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ============================================================
// App
// ============================================================
function App() {
  const currentLoc = sessionStorage.getItem("current_loc");
  const accId = sessionStorage.getItem("acc_id");
  const token = localStorage.getItem("authToken");

  if (!currentLoc || !accId) {
    if (token) {
      initializeSessionValues();
    }
  }

  const location = useLocation();
  const path = location.pathname;
  const { roles } = useRoles();

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch (e) {
      return {};
    }
  })();

  const shouldShowRingCentral =
    !["/login", "/recover-password", "/verify-otp", "/reset-password", "/reset-code-error", "/password-updated"].includes(path) &&
    !path.startsWith("/NA") &&
    !path.startsWith("/inventory") &&
    !roles.includes("Patient");

  return (
    <div className="App">
      <SessionExpirationHandler />
      <ScrollToTop />
      <Toaster containerStyle={{ zIndex: 9999999 }} />
      <NavbarWrapper />
      {/* {shouldShowRingCentral && <RingCentralWidget />} */}
      <NavbarLinksWrapper />
      <div className="main_container" id="main_container">
        <Routes>
          {/* ── Catch-all ── */}
          <Route path="*" element={<NotFound />} />

          {/* ── Public routes (no auth, no capability check) ── */}
          <Route path="/unauthorized" element={<h1>UnAuthorized access</h1>} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          {/* Auth screens share ONE AuthLayout (persistent animated hero);
              only the form card swaps via <Outlet/>. */}
          <Route element={<AuthLayoutRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/recover-password" element={<RecoverPassword />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-code-error" element={<ResetCodeError />} />
            <Route path="/password-updated" element={<PasswordUpdated />} />
          </Route>
          <Route path="/videos/watch/:videoUuid" element={<VideoWatchPage />} />
          <Route path="/help-documentation" element={<HelpDocumentationPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* ── User Settings (auth only, all roles can access) ── */}
          <Route path="/user-settings" element={<UserPage />} />



          {/* ── Dashboard ── */}
          <Route path="/dashboard" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.dashboard" }} fallback="/unauthorized">
                  <Dashboards />
                </RouteAutoGuard>
              }
            />
          </Route>
          {/* ── Dashboard ── */}
          <Route path="/patient-dashboard" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_dashboard" }} fallback="/unauthorized">
                  <PatientDashboard />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── My Tasks ── */}
          <Route path="/my-tasks" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <MyTasksGuard requireNurseTasks={false} fallback="/unauthorized">
                  <MyTasks />
                </MyTasksGuard>
              }
            />
            <Route
              path="out-of-range/escalated-review"
              element={
                <RouteAutoGuard meta={{ feature: "pages.my_tasks" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <EsclatedReview />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="patient/readings"
              element={
                <RouteAutoGuard meta={{ feature: "pages.my_tasks" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <DailiesPatientDetails />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Staff ── */}
          <Route path="/staff" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.admin" }} fallback="/unauthorized">
                  <SatffPage />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Patient pages ── */}
          <Route path="/patient" element={<ProtectedRoute />}>
            <Route
              path="create-patient"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <CreatePatient />
                </RouteAutoGuard>
              }
            />
            <Route
              path="details"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <PatientDetail />
                </RouteAutoGuard>
              }
            />
            {/* PDF views — same patient_detail guard */}
            <Route
              path="care-plan-pdf"
              element={
                <RouteAutoGuard meta={{ feature: "pages.care_plan" }} fallback="/unauthorized">
                  <CareplanViewPdf />
                </RouteAutoGuard>
              }
            />
            <Route
              path="reading-pdf"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <ReadingViewPdf />
                </RouteAutoGuard>
              }
            />
            <Route
              path="audit-report"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <CaseManagerAuditReport />
                </RouteAutoGuard>
              }
            />
            <Route
              path="oor-summary-pdf"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <OORSummaryViewPdf />
                </RouteAutoGuard>
              }
            />
            <Route
              path="out-of-range-pdf"
              element={
                <RouteAutoGuard meta={{ feature: "pages.patient_detail" }} fallback="/unauthorized">
                  <OutOfRangePDFView />
                </RouteAutoGuard>
              }
            />
            <Route
              path="finalize-careplan"
              element={
                <RouteAutoGuard meta={{ feature: "pages.care_plan" }} fallback="/unauthorized">
                  <FinalizeCareplanPDFView />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Admin pages ── */}
          <Route path="/admin" element={<ProtectedRoute />}>
            <Route
              path="dashboard"
              element={

                <RouteAutoGuard meta={{ feature: "pages.admin" }} fallback="/unauthorized">

                  <Admins />
                </RouteAutoGuard>

              }

            />
            <Route
              path="add-organization"
              element={
                <RouteAutoGuard meta={{ feature: "cross_account_access" }} fallback="/unauthorized">
                  <AddOrganization />
                </RouteAutoGuard>
              }
            />
            <Route
              path="locations/:id"
              element={
                <RouteAutoGuard meta={{ feature: "pages.admin" }} fallback="/unauthorized">
                  <LocationDetail />
                </RouteAutoGuard>
              }
            />
            <Route
              path="instructional-videos"
              element={
                <RouteAutoGuard meta={{ feature: "pages.admin" }} fallback="/unauthorized">
                  <InstructionalVideo />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── YouTube Auth Callbacks ── */}
          <Route path="/youtube-auth-success" element={<ProtectedRoute />}>
            <Route index element={<Navigate to="/admin/instructional-videos" replace />} />
          </Route>
          <Route path="/youtube-auth-error" element={<ProtectedRoute />}>
            <Route index element={<Navigate to="/admin/instructional-videos" replace />} />
          </Route>

          {/* ── Executive Dashboard ── */}
          <Route path="/executive-dashboard" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.executive_dashboard" , requiresCEO: false }} fallback="/unauthorized">
                  <SuperAdminDashboardPage />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Billing ── */}
          <Route path="/billings" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.billing" }} fallback="/unauthorized">
                  <BillingPage />
                </RouteAutoGuard>
              }
            />
            <Route
              path="audit-report"
              element={
                <RouteAutoGuard meta={{ feature: "pages.billing" }} fallback="/unauthorized">
                  <CaseManagerAuditReport />
                </RouteAutoGuard>
              }
            />
            <Route
              path="share-audit-report"
              element={
                <RouteAutoGuard meta={{ feature: "cross_account_access" }} fallback="/unauthorized">
                  <ShareAuditReportPage />
                </RouteAutoGuard>
              }
            />
            <Route
              path="billing-report"
              element={
                <RouteAutoGuard meta={{ feature: "pages.billing" }} fallback="/unauthorized">
                  <BillingReportPDF />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Device Orders ── */}
          <Route path="/device-orders" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.device_orders" }} fallback="/unauthorized">
                  <DeviceOrderedPage />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Billing Settings ── */}
          <Route path="/billing-settings" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.billing_settings" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <BillingSettingPage />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Settings / Devices / Reports (coming soon) ── */}
          <Route path="/settings" element={<ProtectedRoute />}>
            <Route index element={<ComingSoon />} />
          </Route>
          <Route path="/devices" element={<ProtectedRoute />}>
            <Route index element={<ComingSoon />} />
          </Route>
          <Route path="/reports" element={<ProtectedRoute />}>
            <Route index element={<ComingSoon />} />
          </Route>

          {/* ── Admin Case Manager ── */}
          <Route path="/admin-case-manager" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerDashboard />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="reading-reviews"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <ReadingReview />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="out-of-range"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerDashboard />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="create-patient"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerCreatePatient />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="escalated-review"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <EsclatedReview />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Case Manager ── */}
          <Route path="/case-manager" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerDashboard />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="reading-reviews"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <ReadingReview />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="out-of-range"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerDashboard />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="create-patient"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <CaseManagerCreatePatient />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
            <Route
              path="escalated-review"
              element={
                <RouteAutoGuard meta={{ feature: "pages.case_manager" }} fallback="/unauthorized">
                  <MedicalBillerGuard>
                    <EsclatedReview />
                  </MedicalBillerGuard>
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Office Inventory ── */}
          <Route path="/office-inventory" element={<ProtectedRoute />}>
            <Route
              index
              element={
                <RouteAutoGuard meta={{ feature: "pages.inventory" }} fallback="/unauthorized">
                  <OfficeInventory />
                </RouteAutoGuard>
              }
            />
          </Route>

          {/* ── Inventory (full layout) ── */}
          <Route path="/inventory" element={<ProtectedRoute />}>
            <Route
              element={
                <RouteAutoGuard meta={{ feature: "pages.inventory" }} fallback="/unauthorized">
                  <InventoryLayout />
                </RouteAutoGuard>
              }
            >
              <Route path="dashboard" element={<InventoryDashboardPage />} />
              <Route path="devices" element={<DeviceManagementPage />} />
              <Route path="supplies" element={<SupplyManagementPage />} />
              <Route path="reports" element={<DeviceManagementPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="warranty" element={<WarrantyPage />} />
              <Route path="support" element={<ReportsPage />} />
              <Route path="usage" element={<ComponentUsageGuide />} />
              <Route path="device-search" element={<DeviceSearchResultsPage />} />
              <Route path="device-distribution" element={<TenantDeviceDistributionPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>

        </Routes>
      </div>
    </div>
  );
}

// ============================================================
// NavbarWrapper
// ============================================================
function NavbarWrapper() {
  const location = useLocation();
  const { roles } = useRoles();
  const routeConfig = routeConfigurations(roles);

  const isExcluded = routeConfig.hideNavbar.some((route) =>
    matchPath({ path: route }, location.pathname)
  );

  if (isExcluded) return null;
  return <Navbar />;
}

// ============================================================
// NavbarLinksWrapper
// ============================================================
function NavbarLinksWrapper() {
  const location = useLocation();
  const { roles } = useRoles();
  const routeConfig = routeConfigurations(roles);

  const excludedRoutes = [
    ...routeConfig.hideNavbar,
    ...routeConfig.hideNavbarLinks,
  ];

  const isExcluded = excludedRoutes.some((route) =>
    matchPath({ path: route }, location.pathname)
  );

  const isDeclared = routeConfig.declaredRoutes.some((route) =>
    matchPath({ path: route }, location.pathname)
  );

  if (isExcluded || !isDeclared) return null;
  return <NavbarLinks />;
}

export default App;

// in development user following scripts in package.json file:
// "scripts": {
//     "start": "react-scripts --openssl-legacy-provider start",
//     "build": "react-scripts build",
//     "test": "react-scripts test",
//     "eject": "react-scripts eject"
// }

// on production user following scripts in package.json file:
// "scripts": {
//   "start": "NODE_OPTIONS='--openssl-legacy-provider --max_old_space_size=4096' react-scripts start",
//   "build": "NODE_OPTIONS='--openssl-legacy-provider --max_old_space_size=4096' react-scripts build",
//   "test": "react-scripts test",
//   "eject": "react-scripts eject"
// }

//

// on production user following scripts in package.json file:
// "scripts": {
//   "start": "NODE_OPTIONS='--openssl-legacy-provider --max_old_space_size=4096' react-scripts start",
//   "build": "NODE_OPTIONS='--openssl-legacy-provider --max_old_space_size=4096' react-scripts build",
//   "test": "react-scripts test",
//   "eject": "react-scripts eject"
// }